import { Alert, Autocomplete, Box, Chip, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { StyledTextField } from "../../../../components/forms/styles/formFieldStyles";

const WizConfigPage = () => {
  const queryClient = useQueryClient();
  const { fetchWizProjects, getWizConfiguration, updateWizConfiguration } =
    useAPI();

  const [pendingChanges, setPendingChanges] = useState(null);

  const {
    data: projectsData,
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: ["wiz", "projects"],
    queryFn: async () => {
      const response = await fetchWizProjects();
      return response.data.projects;
    },
  });

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ["wiz", "configuration"],
    queryFn: async () => {
      const response = await getWizConfiguration();
      return response.data.selected_project_ids;
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: updateWizConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wiz", "configuration"] });
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      Toast.success("Wiz configuration updated successfully");
      setPendingChanges(null);
    },
    onError: (error) => {
      if (error.response) {
        Toast.error(
          `Failed to update configuration: ${error.response.data.detail}`
        );
      } else {
        Toast.error("An error occurred while updating configuration");
      }
    },
  });

  const selectedProjects = pendingChanges ?? configData ?? [];
  const hasChanges = pendingChanges !== null;

  const handleProjectChange = (event, newValue) => {
    setPendingChanges(newValue.map((project) => project.id));
  };

  const handleSave = () => {
    if (pendingChanges !== null) {
      updateConfigMutation.mutate(pendingChanges);
    }
  };

  if (projectsLoading || configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <LoadingIcon size={25} />
      </Box>
    );
  }

  if (projectsError || configError) {
    return (
      <Alert severity="error">
        Error loading Wiz configuration. Please try again later.
      </Alert>
    );
  }

  const projectOptions = projectsData || [];
  const selectedProjectObjects = projectOptions.filter((project) =>
    selectedProjects.includes(project.id)
  );

  return (
    <Box width={700}>
      <Typography variant="h4" mb={2}>
        Configure Wiz Integration
      </Typography>

      <Typography variant="body2" mb={2}>
        Select which Wiz projects you want to monitor. If no projects are
        selected, data will be collected from all available projects.
      </Typography>

      {selectedProjects.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No projects selected - monitoring all projects
        </Alert>
      )}

      <Box mb={3}>
        <Autocomplete
          multiple
          options={projectOptions}
          value={selectedProjectObjects}
          onChange={handleProjectChange}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={
                <Typography variant="caption">Select Wiz Projects</Typography>
              }
              placeholder={
                selectedProjects.length === 0
                  ? "All projects"
                  : "Add more projects"
              }
              margin="dense"
              fullWidth
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </Box>

      <Box display="flex" justifyContent="flex-start" gap={2}>
        <PrimaryButton
          onClick={handleSave}
          disabled={!hasChanges || updateConfigMutation.isPending}
        >
          {updateConfigMutation.isPending ? "Saving..." : "Save Configuration"}
        </PrimaryButton>
        {hasChanges && (
          <Typography variant="body2" color="text.secondary" alignSelf="center">
            You have unsaved changes
          </Typography>
        )}
      </Box>

      {selectedProjects.length > 0 && (
        <Box mt={3}>
          <Typography variant="body2" color="text.secondary">
            Currently monitoring {selectedProjects.length} project
            {selectedProjects.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default WizConfigPage;
